import {useState} from "react";
import {
    useDomainBulkExtensionMutationMutation,
    useAvailableTranslationsQuery,
    useDomainBulkExtensionQueryLazyQuery,
} from "./bulkextensionqueries.generated";
import {useLocationsQuery} from "@/queries.generated";
import {Button, CircularProgress, IconButton, Stack} from "@mui/material";
import {defineMessage, defineMessages, useIntl} from "react-intl";
import {BulkUserModificationError} from "@/api";
import Section from "@/components/Section";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {useDispatch} from "react-redux";
import {showNotification} from "@/actions";
import {PendingWrapper, FormActionButtons, FormattedFormRow, FormContainer, msg_submit} from "@dvrt/graphql-widgets";
import {DateTime} from "luxon";
import {msg_clear} from "@/utils/translations";
import {ContextFilters, useAuth} from "@/context/AuthProvider";
import {useDomainNameQuery} from "@/hooks";
import {BulkUserErrorTable} from "./ErrorTable";
import {downloadExcel, ExcelPermissions, mapFileContentToLocations, mapFileContentToUsers} from "./excel";

const sectionMessages = defineMessages({
    modification: {
        id: "bulk_user_modification.sections.modification",
        defaultMessage: "Modification",
    },
    errors: {
        id: "bulk_user_modification.sections.errors",
        defaultMessage: "Errors during import",
    },
});

type Props = {
    domainId: number;
};

/**
 * Frontend for the bulk extension modification feature.
 *
 * The user can download an excel file with the current extension data and upload it with the modifications.
 *
 * @param domainId The id of the domain to modify
 */
const DomainBulkExtension = ({domainId}: Props) => {
    const auth = useAuth();
    const intl = useIntl();
    const [file, setFile] = useState<File | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [mutationErrors, setMutationErrors] = useState<BulkUserModificationError[]>([]);
    const dispatch = useDispatch();
    const {domainName} = useDomainNameQuery(domainId);

    const translationQuery = useAvailableTranslationsQuery();

    // Using lazy to query only when downloading the excel and when uploading the file (to compare them to the latest data)
    const [extensionsQuery] = useDomainBulkExtensionQueryLazyQuery({
        variables: {domainId},
        fetchPolicy: "no-cache",
    });

    const [bulkExtensionMutation] = useDomainBulkExtensionMutationMutation();

    const locationsQuery = useLocationsQuery({variables: {domainId}});

    if (translationQuery.loading || locationsQuery.loading) return <CircularProgress size={20} />;

    const getProfileNames = (): string[] => {
        return [
            intl.formatMessage({id: "extension.default_profile_name_1", defaultMessage: "Office"}),
            intl.formatMessage({id: "extension.default_profile_name_2", defaultMessage: "Home"}),
            intl.formatMessage({id: "extension.default_profile_name_3", defaultMessage: "Holiday"}),
        ];
    };

    const handleDownload = async (): Promise<void> => {
        setDownloading(true);

        const result = await extensionsQuery();
        const locationsResult = await locationsQuery.refetch();
        if (result.error || locationsResult.error) {
            dispatch(
                showNotification(defineMessage({id: "unexpected_error", defaultMessage: "Unexpected error"}), "error"),
            );
        } else {
            const contextFilter = ContextFilters.domain(domainName);
            const can_add_users = auth.hasPermission("add_extension", contextFilter);

            const permissions: ExcelPermissions = {
                canModifyUsers: auth.hasPermission("edit_extension", contextFilter),
                canManageProvisionedDevices: auth.hasPermission("manage_provisioned_device", contextFilter),
                canAddUsers: can_add_users,
                canAddLocations: auth.hasPermission("add_location", contextFilter),
                canAddUserLogins: auth.hasPermission("manage_user_logins", contextFilter),
                canMoveUsers: can_add_users, // we use the same permission for adding logins
            };

            await downloadExcel(
                result.data?.usersBulk ?? [],
                translationQuery.data.availableTranslations ?? [],
                locationsResult.data.locations.items ?? [],
                result.data?.vendors?.map((v) => v.name) ?? [],
                result.data?.domainAllocation.userPackages ?? [],
                intl,
                {
                    domainName: result.data?.domains.items[0].name,
                    timeOfGeneration: DateTime.now(),
                },
                permissions,
            );
        }

        setDownloading(false);
    };

    const submit = async () => {
        setSubmitting(true);
        setMutationErrors([]);

        const query = await extensionsQuery();

        const result = await bulkExtensionMutation({
            variables: {
                domainId,
                profileNames: getProfileNames(),
                users: await mapFileContentToUsers(file, query.data?.usersBulk ?? []),
                locations: await mapFileContentToLocations(file),
            },
        });

        setSubmitting(false);

        if (result.data?.bulkUserModification?.errors?.length) {
            setMutationErrors(result.data.bulkUserModification.errors);
            return;
        } else {
            dispatch(
                showNotification(
                    defineMessage({
                        id: "bulk_user_modification.success",
                        defaultMessage:
                            "Successfully created {c_count} and updated {u_count} users, and created {c_locations} and updated {u_locations} locations.",
                    }),
                    "success",
                    {
                        c_count: result.data?.bulkUserModification?.users.created,
                        u_count: result.data?.bulkUserModification?.users.updated,
                        c_locations: result.data?.bulkUserModification?.locations.created,
                        u_locations: result.data?.bulkUserModification?.locations.updated,
                    },
                ),
            );
        }

        setFile(null);
    };

    return (
        <Stack spacing={3}>
            <Section label={sectionMessages.modification}>
                <FormContainer style={{width: "600"}}>
                    <FormattedFormRow
                        label={defineMessage({
                            id: "bulk_user_modification.download",
                            defaultMessage: "Download current data",
                        })}
                    >
                        <PendingWrapper pending={downloading}>
                            <IconButton size="large" onClick={handleDownload}>
                                <FontAwesomeIcon icon="download" />
                            </IconButton>
                        </PendingWrapper>
                    </FormattedFormRow>
                    <FormattedFormRow label={defineMessage({id: "modified_file", defaultMessage: "Modified file"})}>
                        <Button component="label">
                            {file?.name ?? intl.formatMessage({id: "notSelected", defaultMessage: "Not selected"})}
                            <input
                                hidden
                                type="file"
                                accept=".xlsx"
                                onChange={(e) => {
                                    if (e.target.files) {
                                        setFile(e.target.files[0]);
                                        // the target file is handled by the component, not the input
                                        e.currentTarget.value = null;
                                    }
                                }}
                            />
                        </Button>
                    </FormattedFormRow>
                    <FormattedFormRow>
                        <div style={{marginTop: "1rem"}}>
                            <FormActionButtons
                                mainAction={submit}
                                secondaryAction={() => {
                                    setFile(null);
                                    setMutationErrors([]);
                                }}
                                mainButtonText={msg_submit}
                                secondaryButtonText={msg_clear}
                                enabled={file !== null}
                                pending={submitting}
                            />
                        </div>
                    </FormattedFormRow>
                </FormContainer>
            </Section>
            {mutationErrors.length > 0 && (
                <Section label={sectionMessages.errors}>
                    <BulkUserErrorTable errors={mutationErrors} />
                </Section>
            )}
        </Stack>
    );
};

export default DomainBulkExtension;
